/**
 * Shared pipeline-model helpers: which catalog models a pipeline may point at
 * (by scoping category), pricing formatting, and the safety-path pipeline set.
 *
 * The filtering rules mirror the P3-C backend's own validation — the dropdown
 * only ever offers models the PUT would accept, so a scoping violation is a
 * server-side backstop, not the primary UX.
 */

import { ModelCatalogRow, PipelineCategory } from '../../../services/quillAdminService';

/**
 * Vision pipelines require an image input modality on the chosen model.
 *
 * `photo_face_assist` was removed (2026-08-03 pipeline-model audit, finding 4):
 * migration 523 seeded the registry row from the P2 photo design but no engine
 * call site was ever written for it, so choosing a model there changed nothing.
 * Migration 571 deletes the row and the admin backend dropped the key from its
 * own VISION_PIPELINES set. If the face-assist pass ships, add it back here in
 * the same change that wires it.
 */
export const VISION_PIPELINE_KEYS = ['photo_activity', 'image_caption'];

/**
 * Safety-path pipelines whose model change triggers the 428 vulnerable-user
 * re-verification gate (G3). Editing either re-PUTs with confirm_safety:true.
 */
export const SAFETY_PIPELINE_KEYS = ['guardrails_classifier', 'refusal_generation'];

export function isVisionPipeline(pipelineKey: string): boolean {
  return VISION_PIPELINE_KEYS.includes(pipelineKey);
}

export function isSafetyPipeline(pipelineKey: string): boolean {
  return SAFETY_PIPELINE_KEYS.includes(pipelineKey);
}

function isXai(row: ModelCatalogRow): boolean {
  return (row.provider ?? 'xai').toLowerCase() === 'xai';
}

function isAnthropic(row: ModelCatalogRow): boolean {
  return (row.provider ?? '').toLowerCase() === 'anthropic';
}

/**
 * Categories where the Anthropic dropdown is disabled ("xAI only"):
 *  - scoped_text_xai           → safety path, hardwired to the xAI endpoint.
 *  - scoped_vision_noanthropic → vision, Anthropic excluded by policy.
 *  - scoped_realtime           → grok-voice only.
 *  - fixed                     → immutable.
 */
export const ANTHROPIC_DISABLED_CATEGORIES: PipelineCategory[] = [
  'scoped_text_xai',
  'scoped_vision_noanthropic',
  'scoped_realtime',
  'fixed',
];

export function isAnthropicAllowed(category: PipelineCategory): boolean {
  return !ANTHROPIC_DISABLED_CATEGORIES.includes(category);
}

function hasImageInput(row: ModelCatalogRow): boolean {
  return (row.input_modalities ?? []).some((m) => m.toLowerCase() === 'image');
}

/**
 * Text-capable: accepts a text prompt. Rows with no declared modalities are
 * assumed text-capable (the older sync source omitted the column); rows that
 * declare modalities must include 'text' (excludes audio-only grok-voice*).
 */
function hasTextInput(row: ModelCatalogRow): boolean {
  const mods = row.input_modalities;
  if (!mods || mods.length === 0) return true;
  return mods.some((m) => m.toLowerCase() === 'text');
}

/**
 * The catalog models offered for a given pipeline row:
 *  - fixed                     → none (edited read-only, no dropdown).
 *  - scoped_realtime           → xAI grok-voice* only.
 *  - scoped_vision_noanthropic → xAI vision only (Anthropic excluded).
 *  - scoped_text_xai           → xAI text-capable only (Anthropic excluded). The
 *                                vulnerable-user safety path (guardrails_classifier
 *                                / refusal_generation) is hardwired to the xAI
 *                                endpoint and cannot honor Anthropic, so the
 *                                dropdown must never offer a Claude model.
 *  - selectable                → all non-deprecated; vision pipelines require an
 *                                image input modality.
 * Deprecated rows are always excluded.
 */
export function filterCatalogForPipeline(
  catalog: ModelCatalogRow[],
  category: PipelineCategory,
  pipelineKey: string
): ModelCatalogRow[] {
  const live = catalog.filter((r) => !r.deprecated_at);

  switch (category) {
    case 'fixed':
      return [];
    case 'scoped_realtime':
      return live.filter((r) => isXai(r) && r.model_id.toLowerCase().startsWith('grok-voice'));
    case 'scoped_vision_noanthropic':
      return live.filter((r) => isXai(r) && hasImageInput(r));
    case 'scoped_text_xai':
      return live.filter((r) => isXai(r) && hasTextInput(r));
    case 'selectable':
      return isVisionPipeline(pipelineKey) ? live.filter(hasImageInput) : live;
    default:
      return live;
  }
}

/**
 * The xAI ("Grok") dropdown options for a pipeline — the combined valid set,
 * narrowed to xAI. Sorted by model_id for a stable menu order.
 */
export function grokOptionsForPipeline(
  catalog: ModelCatalogRow[],
  category: PipelineCategory,
  pipelineKey: string
): ModelCatalogRow[] {
  return filterCatalogForPipeline(catalog, category, pipelineKey)
    .filter(isXai)
    .sort((a, b) => a.model_id.localeCompare(b.model_id));
}

/**
 * The Anthropic ("Claude") dropdown options for a pipeline. Empty for any
 * Anthropic-disabled category (the dropdown is greyed out in that case).
 */
export function anthropicOptionsForPipeline(
  catalog: ModelCatalogRow[],
  category: PipelineCategory,
  pipelineKey: string
): ModelCatalogRow[] {
  if (!isAnthropicAllowed(category)) return [];
  return filterCatalogForPipeline(catalog, category, pipelineKey)
    .filter(isAnthropic)
    .sort((a, b) => a.model_id.localeCompare(b.model_id));
}

/**
 * The combined Default dropdown options for a pipeline (both providers, all
 * valid models for the category). Sorted provider-then-model for grouping.
 */
export function defaultOptionsForPipeline(
  catalog: ModelCatalogRow[],
  category: PipelineCategory,
  pipelineKey: string
): ModelCatalogRow[] {
  return filterCatalogForPipeline(catalog, category, pipelineKey).sort((a, b) => {
    const pa = (a.provider ?? 'xai').localeCompare(b.provider ?? 'xai');
    return pa !== 0 ? pa : a.model_id.localeCompare(b.model_id);
  });
}

/** Same shape as ModelCatalogTable.formatPricing, reused on dropdown entries. */
export function formatModelPricing(row: ModelCatalogRow): string {
  if (row.pricing_amount === null || row.pricing_amount === undefined) return '';
  const amount =
    row.pricing_amount < 1
      ? `$${row.pricing_amount.toFixed(4)}`
      : `$${row.pricing_amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  return row.pricing_unit ? `${amount} / ${row.pricing_unit}` : amount;
}

const CATEGORY_LABEL: Record<PipelineCategory, string> = {
  selectable: 'selectable',
  scoped_realtime: 'realtime (xAI voice)',
  scoped_vision_noanthropic: 'vision (xAI only)',
  scoped_text_xai: 'xAI only (safety)',
  fixed: 'fixed',
};

export function categoryLabel(category: PipelineCategory): string {
  return CATEGORY_LABEL[category] ?? category;
}
